/**
 * Core asset identification types
 * These are the foundational types used across the entire system
 */

/**
 * Asset ID format: ADDRESS:CHAIN_ID
 * e.g. EVM: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2:1"
 * e.g. Solana: "So11111111111111111111111111111111111111112:solana"
 */
export type AssetIdFormat = `${string}:${string}`;
export type EthOrSolanaAssetIdFormat = AssetIdFormat;

/**
 * Represents an unset/empty asset ID
 */
export type UnsetAssetIdFormat = "";

/**
 * Represents a CAIP-19 asset identifier string (EVM).
 * Examples:
 *  - "eip155:1/erc20:0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
 *  - "eip155:1/slip44:60" (native token)
 */
export type EvmCaip19Id =
  | `eip155:${string}/erc20:${string}`
  | `eip155:${string}/slip44:${string}`;

/**
 * Represents a CAIP-19 asset identifier string (Solana/SVM).
 * Example: "solana:mainnet/spl:So11111111111111111111111111111111111111112"
 */
export type SolanaCaip19Id = `solana:${string}/spl:${string}`;

/**
 * Alias for Solana CAIP-19 identifiers using the SVM naming convention.
 */
export type SvmCaip19Id = SolanaCaip19Id;

/**
 * Represents a CAIP-19 asset identifier string (EVM or Solana).
 */
export type Caip19Id = EvmCaip19Id | SolanaCaip19Id;

/**
 * Asset pair format for swaps: SOURCE_ASSET_TARGET_ASSET
 * Direction matters: first is source, second is target
 * Format: ADDRESS:CHAINID_ADDRESS:CHAINID
 */
export type AssetPairIdFormat = `${string}:${string}_${string}:${string}`;

/**
 * Type guard to check if a string is a valid AssetIdFormat
 * Validates that the string contains at least one colon separator.
 */
export function isAssetIdFormat(value: string): value is AssetIdFormat {
  if (!value || typeof value !== "string") return false;
  const parts = value.split(":");
  return parts.length >= 2 && !!parts[0] && !!parts[1];
}

/**
 * Type guard to check if an array of strings are all valid AssetIdFormats
 */
export function isAssetIdsFormat(value: string[]): value is AssetIdFormat[] {
  return Array.isArray(value) && value.every(isAssetIdFormat);
}
